import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Table,
  Button,
  Icon,
  Modal,
  Label,
  Dropdown,
  Input,
  Grid,
  Segment,
  Statistic,
  Pagination
} from 'semantic-ui-react';
import styled from 'styled-components';
import { iRootState } from '../../store';
import {
  getLenderInsuranceBatches,
  generateLenderInsuranceBatch,
  billLenderInsuranceBatch,
  markBatchAsPaid,
  LenderInsuranceBatch
} from '../../services/lender-insurance';
import { postFileAxios } from '../../services/file';
import { get } from '../../services/request';
import {
  dangerNotification,
  successNotification
} from '../../services/notification';
import { maskMoney } from '../../services/masks';
import moment from 'moment';
import { saveAs } from 'file-saver';
import * as Storage from '../../services/storage';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const JSZip = require('jszip');

const Container = styled.div`
  padding: 20px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #59249b;
  margin: 0;
`;

const StatusLabel = styled(Label) <{ statuscolor: string }>`
  background-color: ${props => props.statuscolor} !important;
  color: white !important;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    color: #333;
    margin-bottom: 10px;
  }
`;

const StatsContainer = styled.div`
  margin-bottom: 20px;
`;

const MonthSelector = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ClickableRow = styled(Table.Row)`
  cursor: pointer;
  &:hover {
    background-color: #f9fafb !important;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const ITEMS_PER_PAGE = 20;

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

type Props = ReturnType<typeof mapState>;

const LenderInsuranceBatchList: React.FC<Props> = ({ user, availableBrokersAndEstates }) => {
  const [batches, setBatches] = useState<LenderInsuranceBatch[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<LenderInsuranceBatch | null>(null);
  const [boletoUrl, setBoletoUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [billing, setBilling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
  const [selectedYear, setSelectedYear] = useState(moment().year());

  const location = useLocation<{ filterEstateId?: string; filterMonth?: number; filterYear?: number }>();

  const isAdmin = !user.estate?.id;
  const estateOptions = availableBrokersAndEstates?.estates || [];

  // Filters - initialize from navigation state if available
  // Estate dropdown values are full estate objects, so we need to find the matching object
  const getInitialEstateFilter = () => {
    const navEstateId = location.state?.filterEstateId;
    if (!navEstateId) return '';
    const found = estateOptions.find((e: any) => e.value?.id === navEstateId || e.value === navEstateId);
    return found ? found.value : '';
  };

  const [filterEstateId, setFilterEstateId] = useState<any>(getInitialEstateFilter);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth] = useState<number | undefined>(location.state?.filterMonth);
  const [filterYear, setFilterYear] = useState<number | undefined>(location.state?.filterYear);

  // Bill Modal States
  const [billDueDate, setBillDueDate] = useState('');
  const [billValue, setBillValue] = useState('');
  const [billStatus, setBillStatus] = useState('');
  const [boletoFile, setBoletoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const statusOptions = [
    { key: 'PENDING', text: 'Pendente', value: 'PENDING' },
    { key: 'BILLED', text: 'Faturado', value: 'BILLED' },
    { key: 'PAID', text: 'Pago', value: 'PAID' },
    { key: 'OVERDUE', text: 'Vencido', value: 'OVERDUE' },
    { key: 'CANCELLED', text: 'Cancelado', value: 'CANCELLED' }
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    key: i + 1,
    text: moment().month(i).format('MMMM'),
    value: i + 1
  }));

  const yearOptions = Array.from({ length: 1 }, (_, i) => ({
    key: moment().year() - i,
    text: String(moment().year() - i),
    value: moment().year() - i
  }));

  const fetchData = async (page = currentPage) => {
    try {
      setLoading(true);
      const estateId = user.estate?.id || (filterEstateId?.id || filterEstateId);

      const result = await getLenderInsuranceBatches({
        estateId,
        status: filterStatus,
        month: filterMonth,
        year: filterYear,
        page,
        limit: ITEMS_PER_PAGE,
      });
      setBatches(result.data);
      setTotalCount(result.count);
    } catch (error) {
      dangerNotification('Erro', 'Erro ao carregar lotes');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterEstateId('');
    setFilterStatus('');
    setFilterMonth(undefined);
    setFilterYear(undefined);
  };

  // Trigger fetch when Clear is clicked? Let's implement handles separately.
  const handleFilter = () => {
    setCurrentPage(1);
    fetchData(1);
  };

  const handlePageChange = (_: any, data: any) => {
    const page = Number(data.activePage);
    setCurrentPage(page);
    fetchData(page);
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.estate?.id]); // Only initial load

  const handleBill = async () => {
    if (!selectedBatch) return;

    try {
      setBilling(true);
      let finalBoletoUrl = boletoUrl;

      if (boletoFile) {
        const formData = new FormData();
        formData.append('file', boletoFile);

        await postFileAxios(formData, {
          tableName: 'lender_insurance_batches',
          registerId: selectedBatch.id,
          name: 'boleto.pdf'
        });

        // Use the proxy endpoint as the stored URL
        finalBoletoUrl = `/api/lender-insurance-batches/${selectedBatch.id}/boleto`;
      }

      await billLenderInsuranceBatch(
        selectedBatch.id,
        finalBoletoUrl,
        billDueDate,
        Number(billValue),
        billStatus
      );
      successNotification('Sucesso', 'Lote atualizado com sucesso');
      setBillModalOpen(false);
      setSelectedBatch(null);
      setBoletoUrl('');
      setBillDueDate('');
      setBillValue('');
      setBillStatus('');
      setBoletoFile(null);
      fetchData();
    } catch (error) {
      console.error(error);
      dangerNotification('Erro', 'Erro ao atualizar lote');
    } finally {
      setBilling(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedBatch) return;

    try {
      setPaying(true);
      await markBatchAsPaid(selectedBatch.id);
      successNotification('Sucesso', 'Lote marcado como pago');
      setPayModalOpen(false);
      setSelectedBatch(null);
      fetchData();
    } catch (error) {
      dangerNotification('Erro', 'Erro ao marcar como pago');
    } finally {
      setPaying(false);
    }
  };

  const handleDownload = (batchId: string) => {
    window.open(`${process.env.REACT_APP_API_URL}/lender-insurance-batches/${batchId}/spreadsheet`, '_blank');
  };

  const handleExportZip = async () => {
    try {
      setExporting(true);

      // Fetch ALL batches matching the current filter (no pagination limit)
      const estateId = user.estate?.id || (filterEstateId?.id || filterEstateId);
      const result = await getLenderInsuranceBatches({
        estateId,
        status: filterStatus,
        month: filterMonth,
        year: filterYear,
        page: 1,
        limit: 99999, // Get all matching items
      });

      const allBatches = result.data;

      if (allBatches.length === 0) {
        dangerNotification('Atenção', 'Nenhum lote encontrado para exportar.');
        return;
      }

      const zip = new JSZip();
      const token = Storage.get('id_token');

      // Download each spreadsheet and add to ZIP
      let successCount = 0;
      for (const batch of allBatches) {
        try {
          const url = `${process.env.REACT_APP_API_URL}/lender-insurance-batches/${batch.id}/spreadsheet`;
          const response = await fetch(url, {
            headers: {
              Authorization: token || '',
            },
          });

          if (!response.ok) {
            console.warn(`Falha ao baixar planilha do lote ${batch.id}`);
            continue;
          }

          const blob = await response.blob();
          const estateName = (batch.estate?.name || 'imob').replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_');
          const monthStr = String(batch.referenceMonth).padStart(2, '0');
          const filename = `lote_${estateName}_${monthStr}_${batch.referenceYear}.xlsx`;

          zip.file(filename, blob);
          successCount++;
        } catch (err) {
          console.warn(`Erro ao processar lote ${batch.id}:`, err);
        }
      }

      if (successCount === 0) {
        dangerNotification('Erro', 'Nenhuma planilha pôde ser exportada.');
        return;
      }

      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const now = moment().format('YYYY-MM-DD_HH-mm');
      saveAs(zipBlob, `lotes_prestamista_${now}.zip`);

      successNotification('Sucesso', `${successCount} planilha(s) exportada(s) com sucesso.`);
    } catch (error) {
      console.error('Erro ao exportar ZIP:', error);
      dangerNotification('Erro', 'Erro ao gerar arquivo ZIP.');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '#f2711c';
      case 'BILLED':
        return '#2185d0';
      case 'PAID':
        return '#21ba45';
      case 'OVERDUE':
        return '#db2828';
      case 'CANCELLED':
        return '#767676';
      default:
        return '#767676';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendente';
      case 'BILLED':
        return 'Faturado';
      case 'PAID':
        return 'Pago';
      case 'OVERDUE':
        return 'Vencido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const stats = {
    total: totalCount,
    pending: batches.filter(b => b.status === 'PENDING').length,
    billed: batches.filter(b => b.status === 'BILLED').length,
    paid: batches.filter(b => b.status === 'PAID').length,
    totalValue: batches
      .filter(b => b.status !== 'CANCELLED')
      .reduce((sum, b) => sum + Number(b.adjustedValue || b.calculatedValue), 0)
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handleRowClick = (batch: LenderInsuranceBatch) => {
    setSelectedBatch(batch);
    setDetailsModalOpen(true);
  };

  const viewBoleto = async (batchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Try to get signer URL
      const response = await get(`/lender-insurance-batches/${batchId}/boleto`);
      if (response && response.url) {
        window.open(response.url, '_blank');
      } else {
        dangerNotification('Erro', 'Link do boleto não encontrado.');
      }
    } catch (error) {
      dangerNotification('Erro', 'Erro ao abrir boleto.');
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Lotes de Seguro Prestamista</Title>
        </Header>
        <p>Carregando...</p>
      </Container>
    );
  }

  return (
    <Container>

      <StatsContainer>
        <Segment>
          <Statistic.Group size="small" widths={4}>
            <Statistic>
              <Statistic.Value>{stats.total}</Statistic.Value>
              <Statistic.Label>Total</Statistic.Label>
            </Statistic>
            <Statistic color="orange">
              <Statistic.Value>{stats.pending}</Statistic.Value>
              <Statistic.Label>Pendentes</Statistic.Label>
            </Statistic>
            <Statistic color="blue">
              <Statistic.Value>{stats.billed}</Statistic.Value>
              <Statistic.Label>Faturados</Statistic.Label>
            </Statistic>
            <Statistic color="green">
              <Statistic.Value>{stats.paid}</Statistic.Value>
              <Statistic.Label>Pagos</Statistic.Label>
            </Statistic>
          </Statistic.Group>
        </Segment>
      </StatsContainer>

      <FiltersContainer>
        {isAdmin && (
          <Dropdown
            selection
            search
            placeholder="Imobiliária"
            options={[{ key: 'all', text: 'Todas Imobiliárias', value: '' }, ...estateOptions]}
            value={filterEstateId}
            onChange={(_, { value }) => setFilterEstateId(value as string)}
          />
        )}
        <Dropdown
          selection
          placeholder="Status"
          options={[{ key: 'all', text: 'Todos Status', value: '' }, ...statusOptions]}
          value={filterStatus}
          onChange={(_, { value }) => setFilterStatus(value as string)}
        />
        <Dropdown
          selection
          placeholder="Mês"
          options={[{ key: 'all', text: 'Todos Meses', value: undefined }, ...monthOptions]}
          value={filterMonth}
          onChange={(_, { value }) => {
            setFilterMonth(value as number);
          }}
        />
        <Dropdown
          selection
          placeholder="Ano"
          options={[{ key: 'all', text: 'Todos Anos', value: undefined }, ...yearOptions]}
          value={filterYear}
          onChange={(_, { value }) => setFilterYear(value as number)}
        />
        <Button primary onClick={handleFilter}>
          <Icon name="filter" /> Filtrar
        </Button>
        <Button onClick={() => {
          setFilterEstateId('');
          setFilterStatus('');
          setFilterMonth(undefined);
          setFilterYear(undefined);
        }}>
          Limpar
        </Button>
        <Button
          color="teal"
          loading={exporting}
          disabled={exporting}
          onClick={handleExportZip}
          title="Exportar planilhas dos lotes filtrados em ZIP"
        >
          <Icon name="file archive" /> Exportar
        </Button>
      </FiltersContainer>

      {batches.length === 0 ? (
        <EmptyState>
          <Icon name="archive" size="huge" color="grey" />
          <h3>Nenhum lote encontrado</h3>
        </EmptyState>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Imobiliária</Table.HeaderCell>
                <Table.HeaderCell>Referência</Table.HeaderCell>
                <Table.HeaderCell>Itens</Table.HeaderCell>
                <Table.HeaderCell>Valor</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Vencimento</Table.HeaderCell>
                <Table.HeaderCell>Boleto</Table.HeaderCell>
                <Table.HeaderCell>Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {batches.map(batch => (
                <ClickableRow key={batch.id} onClick={() => handleRowClick(batch)}>
                  <Table.Cell>{batch.estate?.name || '-'}</Table.Cell>
                  <Table.Cell>
                    {String(batch.referenceMonth).padStart(2, '0')}/{batch.referenceYear}
                  </Table.Cell>
                  <Table.Cell>{batch.totalItems}</Table.Cell>
                  <Table.Cell>
                    {maskMoney(
                      String(batch.adjustedValue || batch.calculatedValue),
                      true
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusLabel statuscolor={getStatusColor(batch.status)}>
                      {getStatusLabel(batch.status)}
                    </StatusLabel>
                  </Table.Cell>
                  <Table.Cell>
                    {batch.dueDate ? moment(batch.dueDate).format('DD/MM/YYYY') : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {batch.boletoUrl ? (
                      <span
                        style={{ cursor: 'pointer', color: '#4183c4' }}
                        onClick={(e) => viewBoleto(batch.id, e)}
                        title="Ver Boleto"
                      >
                        <Icon name="file pdf outline" /> Ver
                      </span>
                    ) : (
                      '-'
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      icon
                      size="small"
                      title="Download Planilha"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(batch.id);
                      }}
                    >
                      <Icon name="download" />
                    </Button>
                    {!user.role.includes('ESTATE') && (
                      <Button
                        icon
                        color="blue"
                        size="small"
                        title="Editar / Associar Boleto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBatch(batch);
                          setBoletoUrl(batch.boletoUrl || '');
                          setBoletoFile(null);
                          setBillDueDate(batch.dueDate ? moment(batch.dueDate).format('YYYY-MM-DD') : '');
                          setBillValue(String(batch.adjustedValue || batch.calculatedValue));
                          setBillStatus(batch.status);
                          setBillModalOpen(true);
                        }}
                      >
                        <Icon name="edit" />
                      </Button>
                    )}
                    {/* Removido o check de status PENDING pois admin pode editar a qualquer momento */}
                    {batch.status === 'BILLED' && !user.role.includes('ESTATE') && (
                      <Button
                        icon
                        color="green"
                        size="small"
                        title="Marcar como Pago"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBatch(batch);
                          setPayModalOpen(true);
                        }}
                      >
                        <Icon name="check" />
                      </Button>
                    )}
                  </Table.Cell>
                </ClickableRow>
              ))}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <PaginationContainer>
              <span style={{ color: '#666', fontSize: '14px' }}>
                Exibindo {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} de {totalCount} lotes
              </span>
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                firstItem={null}
                lastItem={null}
                siblingRange={1}
                boundaryRange={1}
              />
            </PaginationContainer>
          )}
        </>
      )
      }

      {/* Modal Associar Boleto */}
      <Modal size="small" open={billModalOpen} onClose={() => setBillModalOpen(false)}>
        <Modal.Header>
          <Icon name="edit" /> Editar Lote / Associar Boleto
        </Modal.Header>
        <Modal.Content>
          <p>Lote: {selectedBatch?.referenceMonth}/{selectedBatch?.referenceYear}</p>

          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <label>Valor (R$)</label>
                <Input
                  fluid
                  type="number"
                  step="0.01"
                  value={billValue}
                  onChange={(_, { value }) => setBillValue(value)}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <label>Vencimento</label>
                <Input
                  fluid
                  type="date"
                  value={billDueDate}
                  onChange={(_, { value }) => setBillDueDate(value)}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <label>Status</label>
                <Dropdown
                  fluid
                  selection
                  options={statusOptions}
                  value={billStatus}
                  onChange={(_, { value }) => setBillStatus(value as string)}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <label>Boleto</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <Button
                    icon="upload"
                    content="Selecionar Arquivo"
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setBoletoFile(file);
                    }}
                    accept=".pdf, .png, .jpg, .jpeg"
                  />
                  {boletoFile && (
                    <div style={{ display: 'flex', alignItems: 'center', color: 'green' }}>
                      <Icon name="file" /> {boletoFile.name}
                    </div>
                  )}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setBillModalOpen(false)}>Cancelar</Button>
          <Button
            primary
            loading={billing}
            onClick={handleBill}
          >
            <Icon name="check" /> Salvar Alterações
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal Marcar como Pago */}
      <Modal size="small" open={payModalOpen} onClose={() => setPayModalOpen(false)}>
        <Modal.Header>
          <Icon name="check circle" color="green" /> Confirmar Pagamento
        </Modal.Header>
        <Modal.Content>
          <p>Confirma que o pagamento deste lote foi recebido?</p>
          <p>
            <strong>Lote:</strong> {selectedBatch?.referenceMonth}/
            {selectedBatch?.referenceYear}
          </p>
          <p>
            <strong>Valor:</strong>{' '}
            {maskMoney(
              String(
                selectedBatch?.adjustedValue || selectedBatch?.calculatedValue || 0
              ),
              true
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setPayModalOpen(false)}>Cancelar</Button>
          <Button color="green" loading={paying} onClick={handleMarkPaid}>
            <Icon name="check" /> Confirmar Pagamento
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal Detalhes */}
      <Modal
        size="large"
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      >
        <Modal.Header>
          Itens do Lote - {String(selectedBatch?.referenceMonth).padStart(2, '0')}/{selectedBatch?.referenceYear}
        </Modal.Header>
        <Modal.Content scrolling>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Inquilino</Table.HeaderCell>
                <Table.HeaderCell>Imóvel</Table.HeaderCell>
                <Table.HeaderCell>Valor</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {selectedBatch?.items && selectedBatch.items.length > 0 ? (
                selectedBatch.items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <strong>{item.lenderInsurance?.tenantName}</strong>
                      <br />
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        {item.lenderInsurance?.tenantDocument}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{item.lenderInsurance?.propertyAddress}</Table.Cell>
                    <Table.Cell>
                      {maskMoney(String(item.value), true)}
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={3} textAlign="center">
                    Nenhum item encontrado neste lote.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailsModalOpen(false)}>Fechar</Button>
        </Modal.Actions>
      </Modal>
    </Container >
  );
};

export default connect(mapState)(LenderInsuranceBatchList);
